import { env } from "../setup/env";
import type { FilterOption } from "../types/filtermenu";
import type { CommonJob, CommonJobData, JobValues } from "../types/job";
import type { TranslationsMap } from "../types/language";

const JOB_AD_URL_CACHE: Record<string, string> = {};

export interface Counts {
  [key: string]: number;
}

export function removeDuplicates(array: FilterOption[]): FilterOption[] {
  const cleanedArray = array.filter(
    (arr, index, oldArray) =>
      index ===
      oldArray.findIndex(
        (entry) => entry.label === arr.label && entry.value === arr.value
      )
  );

  return cleanedArray;
}

const URL_REPLACEMENTS: Record<string, string> = {
  ä: "ae",
  Ä: "Ae",
  ö: "oe",
  Ö: "Oe",
  ü: "ue",
  Ü: "Ue",
  ß: "ss",
  ":": "_",
  "/": "_",
  "(": "-",
  ",": "-",
  "&": "-",
  "?": "-",
  ")": "",
  "%": "",
  "#": "",
  "*": "",
};

export function slugifyUrlComponent(title: string): string {
  if (!title) {
    return "x";
  }

  const slug = title
    .split("")
    .map((char) => URL_REPLACEMENTS[char] ?? char)
    .join("")
    .trim()
    .replace(/[ -]+/g, "-");

  return slug || "x";
}

export function getJobAdUrl(
  title: string,
  city: string,
  idClient: string,
  legacy?: boolean
): string {
  // Especially slugifyUrlComponent is a little computationally expensive
  // so we just cache the result.
  const cacheKey = `${idClient}_${title}_${city}_${legacy ? "legacy" : "new"}`;
  if (JOB_AD_URL_CACHE[cacheKey]) {
    return JOB_AD_URL_CACHE[cacheKey];
  }

  const titleSlug = slugifyUrlComponent(title);
  const citySlug = slugifyUrlComponent(city);

  const url = legacy
    ? `/job/${titleSlug}-${citySlug}?id=${idClient}`
    : `/job/${titleSlug}-${citySlug}/${idClient}`;

  JOB_AD_URL_CACHE[cacheKey] = url;

  return url;
}

export function getJobAdUrlFromData(jobData: {
  title: string;
  locations: { city: string }[];
  idClient: string;
}) {
  return getJobAdUrl(
    jobData.title,
    jobData.locations[0].city,
    jobData.idClient
  );
}

const regEx = new RegExp(
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
);
// Function to validate an email address format
export function validateEmail(email: string): boolean {
  return regEx.test(String(email).toLowerCase());
}

export function compare(a: number, b: number) {
  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }
  return 0;
}

export function clamp(
  value: number,
  lowerBound: number,
  upperBound: number
): number {
  return Math.max(lowerBound, Math.min(upperBound, value));
}

/**
 * True modulo that also works for negative numbers.
 *
 * https://stackoverflow.com/a/17323608/5587737
 */
export function mod(n: number, m: number): number {
  return ((n % m) + m) % m;
}

/**
 * Get the keys of an object.
 * Like Object.keys() but with better types.
 */
export function keysOfObject<T extends object>(v: T): (string & keyof T)[] {
  // TypeScript kinda uses the wrong type for Object.keys()
  // This wound be the more accurate type
  // Object.keys returns a list of strings and the strings are also keys of T
  return Object.keys(v) as (string & keyof T)[];
}

/**
 * Access object using a "path", e.g. "data.location.country"
 * Imitates Algolia's behavior when it comes to arrays.
 *
 */
export function deepAccess(object: unknown, key: string): unknown {
  return deepAccessInternal(object, key.split("."));
}

function deepAccessInternal(object: unknown, keys: string[]): unknown {
  const firstKey = keys.shift();
  if (!firstKey) {
    throw new Error("No key");
  }
  const value = (object as Record<string, unknown>)[firstKey];
  if (keys.length === 0) {
    return value;
  }

  if (typeof value === "undefined" || value === null) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.flatMap((item) => deepAccessInternal(item, [...keys]));
  }

  return deepAccessInternal(value, keys);
}

export function deepObjectEntries<T>(
  obj: Record<string, unknown>
): [string, T][] {
  return Object.entries(obj).flatMap(([k, v]) => {
    if (typeof v === "object" && v !== null) {
      return deepObjectEntries(v as Record<string, unknown>).map(
        ([nestedK, val]) => [`${k}.${nestedK}`, val] as [string, T]
      );
    }

    return [[k, v] as [string, T]];
  });
}

export function cloneAsJson<T>(value: T): T {
  if (typeof value !== "object") {
    throw new Error("Can not clone non object value");
  }

  return JSON.parse(JSON.stringify(value));
}

export function displayJobAttribute(attr: JobValues): string | null {
  if (Array.isArray(attr)) {
    return attr.join(", ");
  }
  if (typeof attr === "number") {
    return String(attr);
  }
  return attr;
}

//Language keys currently only exist for E.ON
export function combineEmploymentTypes(
  employmentType: string | string[],
  text: TranslationsMap
) {
  if (!Array.isArray(employmentType) || employmentType.length === 0) {
    return "Keine Beschäftigungsart angegeben";
  }

  const hasVollzeit = employmentType.includes(
    text.employmentTypeDisplay_fulltime
  );
  const hasTeilzeit = employmentType.includes(
    text.employmentTypeDisplay_parttime
  );
  const hasJobsharing = employmentType.includes("Jobsharing");

  if (hasVollzeit && hasTeilzeit) {
    return hasJobsharing
      ? `${text.employmentTypeDisplay_combined}, Jobsharing`
      : text.employmentTypeDisplay_combined;
  }
  if (hasJobsharing) {
    return "Jobsharing";
  }

  return displayJobAttribute(employmentType);
}

/**
 * Returns the union between the values of two arrays.
 * Or only the unique values.
 */
export function uniqueValues<T>(a: T[], b: T[]): T[] {
  return [...new Set([...a, ...b]).values()];
}

// facetFilterValue, e.g. "data.language:DE"
// The value might contain a colon, e.g. "data.entryLevel:Absolvierende / Berufsanfänger:in",
// so we can't just split by colon.
/** Trailing list separator left on a facet value by an old link or bookmark. */
const TRAILING_SEPARATOR_RE = /[;,]$/;

/**
 * Canonicalises a facet value so both engines filter on the same string.
 * Typesense's `:=` is token-based and ignores surrounding whitespace and a
 * trailing separator; Atlas' `in` on a `token` mapping with
 * `normalizer: "none"` is byte-exact and answers a drifted value with zero
 * hits. Measured on production tkag_de (2026-09-10): a filter carrying
 * "thyssenkrupp Rasselstein GmbH;" found 21 jobs on Typesense and 0 on Atlas.
 *
 * Assumes no facet value legitimately ends in ";" or "," — true for every
 * tkag locale collection as of 2026-09-10, and the reason only the trailing
 * one is stripped: separators inside a value ("Finanzen, Rechnungswesen &
 * Controlling") are part of the name and must survive.
 */
function canonicalFilterValue(valueName: string): string {
  const trimmed = valueName.trim();
  const stripped = trimmed.replace(TRAILING_SEPARATOR_RE, "").trim();
  return stripped.length > 0 ? stripped : trimmed;
}

/** A comma with any amount of whitespace around it. */
const COMMA_SPACING_RE = /\s*,\s*/g;

/**
 * Every byte-exact spelling of one facet value that Atlas' `in` has to accept.
 *
 * Typesense's `:=` compares tokens, so comma spacing is invisible to it:
 * `data.locations.cityState:="Duisburg,Nordrhein-Westfalen"` and the spaced
 * spelling both answer 65 on production tkag_de, where only the spaced form is
 * stored. Atlas' `in` on a `token` mapping compares bytes even with the
 * lowercase normalizer, so it answers the other spelling with zero hits —
 * measured 2026-09-10, a request carrying five unspaced cityState values
 * returned 28 on Typesense and 0 on Atlas.
 *
 * Unlike a trailing separator (see canonicalFilterValue) this cannot be
 * settled by picking one spelling: both occur in the data. 109 of 412 distinct
 * cityState values per locale carry a bare comma, about 101 of them next to a
 * spaced twin of the same city, and `data.company` holds a genuine bare-comma
 * name, "thyssenkrupp Springs & Stabilizers (Pinghu) Co.,Ltd". Canonicalising
 * either way would make the other form unfilterable, so the value as given is
 * always kept and the alternative spellings are added beside it.
 *
 * Scoped to comma spacing, which is the drift that reaches production through
 * old links. Typesense ignores every punctuation difference, so a value that
 * drifts some other way still needs its own case.
 */
export function filterValueVariants(valueName: string): string[] {
  if (!valueName.includes(",")) {
    return [valueName];
  }
  return uniqueValues(
    [valueName],
    [
      valueName.replace(COMMA_SPACING_RE, ", "),
      valueName.replace(COMMA_SPACING_RE, ","),
    ]
  );
}

export function extractFilterValues(filterValueItem: string): {
  filterFacet: string;
  valueName: string;
} {
  const colonIndex = filterValueItem.indexOf(":");

  if (colonIndex === -1) {
    throw new Error(`Invalid value: ${filterValueItem}`);
  }

  const filterFacet = filterValueItem.slice(0, colonIndex);
  const valueName = canonicalFilterValue(filterValueItem.slice(colonIndex + 1));

  return { filterFacet, valueName };
}

export function breakStringAtChar(
  string: string | undefined,
  char: string
): string {
  if (!string || string === "") {
    return "";
  }
  return string.replaceAll(char, `${char}\u200B`);
}

// Adds a zero width space after every slash in the title, but
// doesn't break within the (m/w/d).
export function breakTitlesAtSlash(title: string): string {
  return title.replaceAll("/", "/\u200B").replace("m/\u200Bw/\u200Bd", "m/w/d");
}

export function filterArray<T>(
  array: (T | false | undefined | "" | 0 | null)[]
): T[] {
  return array.filter(Boolean) as T[];
}

export function sumArray<T>(
  array: T[],
  cb: (item: T) => number = Number
): number {
  return array.reduce((sum, item) => sum + cb(item), 0);
}

export function generatePathnameWithLocale(canonicalUrl: string, loc: string) {
  const fragments = canonicalUrl.split("/");

  if (loc === "default") {
    const defaultUrl = fragments.filter((_, i) => i !== 3);
    return defaultUrl.join("/");
  }
  fragments[3] = loc;
  return fragments.join("/");
}

export function findFirstStringIndex(text: string, strings: string[]) {
  for (const str of strings) {
    const index = text.indexOf(str);
    if (index !== -1) {
      return index; // Return the index as soon as one of the strings is found
    }
  }
  return -1; // Or return -1 if none of the strings are found
}

const salaryRegex = /£([\d,]+)(?:\s*-\s*£([\d,]+))?/;
/**
 *
 * @param compensation job.content.compensation as string
 * @param country job.data.country
 * @param locale router.locale e.g. "de"
 * @returns Object with the upper- and lower Bound of a salary range or undefined
 *
 * Works for £ and € right now. Extend if you need other currencies
 */
export function extractSalaryRange(
  compensation: string | undefined,
  country: string,
  locale: string | undefined
): { lowerBound: string; upperBound: string | undefined } | undefined {
  if (!compensation) {
    return;
  }
  const match = compensation.match(salaryRegex);

  if (match) {
    const lowerBound = Number(match[1].replace(/,/g, "")).toLocaleString(
      locale
    );
    const upperBound = match[2]
      ? Number(match[2].replace(/,/g, "")).toLocaleString(locale)
      : undefined;

    if (country === "United Kingdom") {
      return {
        lowerBound: `£${lowerBound}`,
        upperBound: upperBound ? `£${upperBound}` : undefined,
      };
    }
    return {
      lowerBound: `${lowerBound}€`,
      upperBound: upperBound ? `${upperBound}€` : undefined,
    };
  }
  return;
}

// URL params that differ from their Typesense field name
export const urlParamToField: Record<string, string> = {
  data_source: "source",
};

export function toDataDotFilter(
  appliedFilters: Record<string, string[]>
): Record<string, string[]> {
  return Object.fromEntries(
    Object.entries(appliedFilters).map(([key, values]) => {
      const field = urlParamToField[key] ?? key;
      return [
        `data.${field}`,
        values.map((value) => value.replace(key, `data.${field}`)),
      ];
    })
  );
}

export function getCanonicalUrl(
  arg:
    | CommonJob
    | CommonJobData
    | { city: string; title: string; idClient: string }
): string {
  if ("data" in arg) {
    return `${env.NEXT_PUBLIC_BASE_URL}${getJobAdUrl(arg.data.locations[0].city, arg.data.title, arg.data.idClient)}`;
  }

  if ("locations" in arg) {
    return `${env.NEXT_PUBLIC_BASE_URL}${getJobAdUrl(arg.locations[0].city, arg.title, arg.idClient)}`;
  }

  return `${env.NEXT_PUBLIC_BASE_URL}${getJobAdUrl(arg.city, arg.title, arg.idClient)}`;
}

export function getCanonicalUrlShort(idClient: string): string {
  return `${env.NEXT_PUBLIC_BASE_URL}/job/id/${idClient}`;
}

const localeRegex = /https?:\/\/[^/]+\/([a-z]{2}).*/;

export function extractLocaleFromUrl(url: string) {
  const match = url.match(localeRegex);
  if (!match) {
    return;
  }

  return match[1];
}

/**
 * Filters and sorts items based on their availability and position in the DOM.
 * Only items whose IDs exist in the DOM are returned, sorted by their DOM order.
 *
 * @param items - Array of items to filter and sort
 * @param getId - Function to extract the ID from an item
 * @returns Filtered and sorted items in DOM order
 */
export function filterAndSortByDomOrder<T>(
  items: T[],
  getId: (item: T) => string
): T[] {
  // Return items unchanged if not in browser environment (SSR safety)
  if (typeof window === "undefined" || typeof document === "undefined") {
    return items;
  }

  // Create a map for quick item lookup by ID
  const itemMap = new Map(items.map((item) => [getId(item), item]));

  // Find all available IDs in DOM
  const availableIds: string[] = [];
  for (const item of items) {
    const id = getId(item);
    const element = document.getElementById(id);
    if (element) {
      availableIds.push(id);
    }
  }

  // Sort by DOM position using compareDocumentPosition
  availableIds.sort((a, b) => {
    const elementA = document.getElementById(a);
    const elementB = document.getElementById(b);

    if (!(elementA && elementB)) {
      return 0;
    }

    // Compare positions using compareDocumentPosition
    const position = elementA.compareDocumentPosition(elementB);
    // biome-ignore lint/suspicious/noBitwiseOperators: Node.compareDocumentPosition() returns a bitmask; bitwise AND is the API contract
    return position & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
  });

  // Return items in DOM order
  return availableIds
    .map((id) => itemMap.get(id))
    .filter((item): item is T => item !== undefined);
}
